"use client";

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExploreTopProductsProps } from "../../types/explore-top-products.types";
import { hasScrollableContent } from "../../utils/mediaUtils";
import StoryBlock from "../StoryBlock/StoryBlock.client";
import ProductCard from "./ProductCard.client";

const ExploreTopProducts: React.FC<ExploreTopProductsProps> = ({
  productClass,
  listClassName,
  leftCardBgColor,
  rightCardBgColor,
  headingTextStyle,
  borderColor,
  fontWeight,
  containerClassName,
  storyBlockClassName,
  fitViewport = false,
  height,
  mobileHeight,
  posterUrl,
  videoId,
  activeVideoResult,
  products,
  subtitleClass,
  origin,
  sectionTitleCt,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollableRef = useRef<HTMLUListElement>(null);
  const frameRef = useRef<number | null>(null);
  const [hasScroll, setHasScroll] = useState(false);
  const [storyBlockHeight, setStoryBlockHeight] = useState<string | undefined>(undefined);

  const checkScroll = useCallback(() => {
    if (scrollableRef.current) {
      const nextHasScroll = hasScrollableContent(
        scrollableRef.current.scrollWidth,
        scrollableRef.current.clientWidth
      );
      setHasScroll((currentHasScroll) =>
        currentHasScroll === nextHasScroll ? currentHasScroll : nextHasScroll
      );
    }
  }, []);

  const measureInitialLayout = useCallback(() => {
    if (typeof window === "undefined") return;
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
    }

    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      checkScroll();

      if (!fitViewport) return;

      const container = containerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const containerDocumentTop = containerRect.top + window.scrollY;
      const listHeight = scrollableRef.current?.getBoundingClientRect().height ?? 0;
      const computedStyles = window.getComputedStyle(container);
      const paddingTop = Number.parseFloat(computedStyles.paddingTop || "0") || 0;
      const paddingBottom = Number.parseFloat(computedStyles.paddingBottom || "0") || 0;
      const gap = Number.parseFloat(computedStyles.rowGap || computedStyles.gap || "0") || 0;
      const mobileBottomNavOffset = window.innerWidth < 768 ? 72 : 0;

      const availableHeight =
        window.innerHeight -
        containerDocumentTop -
        listHeight -
        paddingTop -
        paddingBottom -
        gap -
        mobileBottomNavOffset -
        8;
      const nextHeight = `${Math.max(0, Math.floor(availableHeight))}px`;

      setStoryBlockHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight
      );
    });
  }, [checkScroll, fitViewport]);

  useEffect(() => {
    measureInitialLayout();

    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [products?.length, measureInitialLayout]);

  const productItems = useMemo(
    () =>
      products?.map((product, index) => {
        const firstUnitUrl = product.product_units?.[0]?.unit_url || "";
        const firstImageSrc = product.product_images?.[0]?.image_src || "";
        const productKey = [
          product.id || "product",
          firstUnitUrl || product.product_name || firstImageSrc || "item",
          index,
        ].join("-");

        return (
          <ProductCard
            key={productKey}
            product={product}
            origin={origin}
            leftCardBgColor={leftCardBgColor}
            rightCardBgColor={rightCardBgColor}
            headingTextStyle={headingTextStyle}
            borderColor={borderColor}
            fontWeight={fontWeight}
            subtitleClass={subtitleClass}
            videoId={videoId}
            sectionTitleCt={sectionTitleCt}
          />
        );
      }),
    [
      products,
      origin,
      leftCardBgColor,
      rightCardBgColor,
      headingTextStyle,
      borderColor,
      fontWeight,
      subtitleClass,
      videoId,
      sectionTitleCt,
    ]
  );

  const storyBlockProps = useMemo(() => {
    if (activeVideoResult?.videoType === "replay" && activeVideoResult?.videoId) {
      return { videoId: activeVideoResult.videoId };
    }
    return { playlist: activeVideoResult?.videoId || videoId || "vVOVee" };
  }, [activeVideoResult, videoId]);

  const isLive = origin === "live";
  const borderColorClass = isLive
    ? "border-[#D3D3D3] border-none"
    : "border-[#4D4D4D] border-t border-r border-b";
  const headingTextColorClass = isLive ? "text-[#1A1A1A]" : "text-white";
  const reservedStoryBlockHeight = mobileHeight ?? height ?? (isLive ? "500px" : "504px");
  const resolvedStoryBlockHeight = fitViewport
    ? (storyBlockHeight ?? reservedStoryBlockHeight)
    : height;

  const storyblock = useMemo(() => {
    return (
      <StoryBlock
        origin={origin as "live" | "influencers"}
        {...storyBlockProps}
        className={storyBlockClassName}
        height={resolvedStoryBlockHeight}
        mobileHeight={fitViewport ? resolvedStoryBlockHeight : mobileHeight}
        posterUrl={posterUrl}
        titleCt={sectionTitleCt}
      />
    );
  }, [
    fitViewport,
    mobileHeight,
    origin,
    posterUrl,
    resolvedStoryBlockHeight,
    sectionTitleCt,
    storyBlockClassName,
    storyBlockProps,
  ]);

  return (
    <div
      ref={containerRef}
      className={`max-w-full ${borderColorClass} ${origin === "influencers" ? "px-[16px]" : ""} pt-[12px] ${fitViewport ? "flex min-h-0 flex-1 flex-col" : ""} ${containerClassName || ""}`}
    >
      {origin == "influencers" && (
        <div>
          <h2
            className={`video-grid__title truncate text-[20px] font-medium ${headingTextColorClass}`}
          >
            Live Session
          </h2>
        </div>
      )}

      {storyblock}

      {products && products.length > 0 && (
        <ul
          ref={scrollableRef}
          aria-label="Top products list"
          className={`mb-2 flex flex-nowrap gap-2 overflow-x-auto overflow-y-hidden ${
            hasScroll ? "justify-start" : "justify-center"
          } ${productClass || "mt-5"} ${listClassName || ""}`}
          style={{
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            WebkitOverflowScrolling: "touch",
            marginLeft: "10px",
            paddingRight: "20px",
          }}
        >
          {productItems}
        </ul>
      )}
    </div>
  );
};

export default memo(ExploreTopProducts);
